import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageViewModel, LookupProxy } from '../../proxy/proxies';
import { firstValueFrom, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
import { LocalStorageService } from './storage/local-storage.service';

@Injectable({
    providedIn: 'root',
})
export class LanguageInitializerService {
    // ReplaySubject to ensure language initialization completes only once and is cached
    private initializationSubject = new ReplaySubject<boolean>(1);
    private initializationComplete = false;
    private _languages: LanguageViewModel[] = [];

    // LocalStorage keys for language management
    private readonly LANGUAGE_GUID_KEY = 'languageGuid';
    private readonly LANGUAGE_ISO1_KEY = 'languageISO1';

    constructor(
        private _translate: TranslateService,
        private _lookupProxy: LookupProxy,
        private _localStorageService: LocalStorageService
    ) {}

    async initializeLanguage(): Promise<boolean> {
        if (this.initializationComplete) {
            return true; // Already initialized, return immediately
        }
        return new Promise<boolean>((resolve) => {
            if (!this.initializationSubject.observed) {
                this.performLanguageInitialization();
            }

            // Subscribe to the initialization subject to get notified when it's done
            this.initializationSubject.subscribe((success) => {
                this.initializationComplete = true;
                resolve(success);
            });
        });
    }

    /**
     * Performs the actual language initialization process
     * Only called once regardless of how many times initializeLanguage is called
     */
    private performLanguageInitialization(): void {
        // Use a safe wrapper around the language API call
        firstValueFrom(
            this._lookupProxy.lookup_GetLanguages().pipe(
                // Handle errors gracefully
                catchError((error) => {
                    console.error('Error initializing language:', error);
                    // Provide fallback language settings
                    this.setFallbackLanguage();
                    return of(null);
                }),
                // Process successful response
                tap((response) => {
                    if (response?.result) {
                        this.configureLanguages(response.result);
                    } else {
                        this.setFallbackLanguage();
                    }
                }),
                // Always mark initialization as complete
                finalize(() => {
                    this.initializationSubject.next(true);
                    this.initializationSubject.complete();
                })
            )
        );
    }

    /**
     * Configure languages based on API response
     */
    private configureLanguages(languages: LanguageViewModel[]): void {
        // Store languages for later use
        this._languages = languages;

        // Migration: Check for old 'language' key and migrate to new structure
        this.migrateOldLanguageKey(languages);

        // Check if language keys exist, if not set defaults
        if (this._localStorageService.getItem(this.LANGUAGE_GUID_KEY) == null) {
            const defaultLang = languages.find((c) => c.isO1 == 'en');

            if (defaultLang) {
                this._localStorageService.setItem(this.LANGUAGE_GUID_KEY, defaultLang.id);
                this._localStorageService.setItem(this.LANGUAGE_ISO1_KEY, defaultLang.isO1);
            } else {
                // Fallback to first language if English not found
                const fallbackLang = languages[0];
                if (fallbackLang) {
                    this._localStorageService.setItem(this.LANGUAGE_GUID_KEY, fallbackLang.id);
                    this._localStorageService.setItem(this.LANGUAGE_ISO1_KEY, fallbackLang.isO1);
                }
            }
        }

        const langIds = languages.map((c) => c.id);
        this._translate.addLangs(langIds ?? []);

        // IMPORTANT: Get current language AFTER localStorage keys are set in configureLanguages
        const currentLangGuid = this.getCurrentLanguageGuid();
        const currentLangISO1 = this.getCurrentLanguageCode();

        if (!this._localStorageService.getItem(this.LANGUAGE_ISO1_KEY)) {
            this._localStorageService.setItem(this.LANGUAGE_ISO1_KEY, currentLangISO1);
        }

        this._translate.setDefaultLang(currentLangGuid);
        this._translate.use(currentLangGuid);
    }

    /**
     * Set fallback language in case of initialization failure
     */
    private setFallbackLanguage(): void {
        const fallbackGuid = '24c9b00d-d028-4e04-a1aa-f04d2dcae2c3';
        const fallbackISO1 = 'en';

        this._localStorageService.setItem(this.LANGUAGE_GUID_KEY, fallbackGuid);
        this._localStorageService.setItem(this.LANGUAGE_ISO1_KEY, fallbackISO1);

        this._translate.addLangs([fallbackGuid]);
        this._translate.setDefaultLang(fallbackGuid);
        this._translate.use(fallbackGuid);
    }

    /**
     * Get the current language GUID from localStorage
     */
    getCurrentLanguageGuid(): string {
        return (
            this._localStorageService.getItem<string>(this.LANGUAGE_GUID_KEY) ?? '24c9b00d-d028-4e04-a1aa-f04d2dcae2c3'
        );
    }

    /**
     * Get the current language ISO1 code from localStorage
     */
    getCurrentLanguageCode(): string {
        return this._localStorageService.getItem<string>(this.LANGUAGE_ISO1_KEY) ?? 'en';
    }

    /**
     * Get available languages from API response
     */
    getLanguages(): LanguageViewModel[] {
        return this._languages;
    }

    /**
     * Update language and reload translations with new configuration URL
     * Updates both languageGuid and languageISO1 synchronously
     */
    setLanguage(languageId: string): void {
        // Find the language object to get both ID and ISO1 code
        const selectedLanguage = this._languages.find((lang) => lang.id === languageId);

        if (selectedLanguage) {
            // Update both localStorage keys synchronously
            this._localStorageService.setItem(this.LANGUAGE_GUID_KEY, selectedLanguage.id);
            this._localStorageService.setItem(this.LANGUAGE_ISO1_KEY, selectedLanguage.isO1);
        } else {
            console.error(`Language with ID ${languageId} not found in available languages`);
            return;
        }

        this._translate.setDefaultLang(languageId);
        this._translate.use(languageId);

        // Reload translations to use the new language's configuration URL
        this._translate.reloadLang(languageId);
    }

    /**
     * Set language by ISO1 code (e.g., 'en', 'tr')
     */
    setLanguageByISO1(iso1Code: string): void {
        const selectedLanguage = this._languages.find((lang) => lang.isO1 === iso1Code);

        if (selectedLanguage) {
            this.setLanguage(selectedLanguage.id);
        } else {
            console.error(`Language with ISO1 code ${iso1Code} not found in available languages`);
        }
    }

    /**
     * Get current language info (both GUID and ISO1)
     */
    getCurrentLanguageInfo(): { guid: string; iso1: string } {
        return {
            guid: this.getCurrentLanguageGuid(),
            iso1: this.getCurrentLanguageCode(),
        };
    }

    /**
     * Migrate from old 'language' localStorage key to new structure
     */
    private migrateOldLanguageKey(languages: any[]): void {
        const oldLanguageKey = this._localStorageService.getItem<string>('language');
        const hasNewKeys = this._localStorageService.getItem(this.LANGUAGE_GUID_KEY) !== null;

        if (oldLanguageKey && !hasNewKeys) {
            // Find the language object by ID
            const languageObj = languages.find((lang) => lang.id === oldLanguageKey);

            if (languageObj) {
                this._localStorageService.setItem(this.LANGUAGE_GUID_KEY, languageObj.id);
                this._localStorageService.setItem(this.LANGUAGE_ISO1_KEY, languageObj.isO_1);

                // Remove the old key to complete migration
                this._localStorageService.removeItem('language');
            }
        }
    }
}
